import LabeledSheet from 'LuxKit/LabeledSheet'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useListTruncator from 'hooks/useListTruncator'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import TourV2HighlightedInclusionItem from './TourV2HighlightedInclusionItem'
import TourV2AllInclusionsModal, { TourV2AllInclusionsModalResult } from './TourVariations/TourV2AllInclusionsModal'
import { partitionBy } from 'lib/array/arrayUtils'
import { getIsLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import { connect } from 'react-redux'

const HighlightedInclusionsContainer = styled(Group)`
  padding: ${rem(16)} ${rem(20)} ${rem(20)};
`

interface MappedStateProps {
  isLuxPlusEnabled: boolean
}

interface Props extends MappedStateProps {
  variation: App.Tours.TourV2OfferVariation
  onCheckAvailability: () => void
}

function TourV2HighlightedInclusions({ variation, onCheckAvailability, isLuxPlusEnabled }: Props) {
  const [flashInclusions, nonFlashInclusions] = useMemo(() => partitionBy(variation.inclusionItems, inclusion => !!inclusion.isFlash), [variation.inclusionItems])
  const nonFlashFilteredInclusions = useMemo(() => {
    if (isLuxPlusEnabled) {
      return nonFlashInclusions
    }
    return nonFlashInclusions.filter(inclusion => !inclusion.isLuxPlusBonus)
  }, [nonFlashInclusions, isLuxPlusEnabled])

  const [bonusInclusions, inclusions] = useMemo(() => partitionBy(nonFlashFilteredInclusions, inclusion => !!inclusion.isLuxPlusBonus), [nonFlashFilteredInclusions])
  const inclusionShow = 3
  const [visible, hidden] = useListTruncator(inclusionShow, [...bonusInclusions, ...flashInclusions, ...inclusions])
  const showModal = useContext(ModalContext)
  const openAllInclusionsModal = useCallback(async() => {
    const result = await showModal<TourV2AllInclusionsModalResult>(<TourV2AllInclusionsModal
      variation={variation}
      primaryActionText="Check availability"
    />)
    if (result === 'primary-action') {
      onCheckAvailability()
    }
  }, [showModal, variation, onCheckAvailability])

  if (!visible.length) return null

  return <LabeledSheet
    kind={isLuxPlusEnabled && bonusInclusions.length ? 'lux-plus' : (flashInclusions.length ? 'exclusive' : 'highlight')}
    label={flashInclusions.length && !bonusInclusions.length ? 'Limited time inclusions' : 'Why you’ll love this tour'}
  >
    <HighlightedInclusionsContainer direction="vertical" gap={12}>
      <Group direction="vertical" gap={4}>
        {visible.map((inclusion) => <TourV2HighlightedInclusionItem
          key={inclusion.id}
          inclusion={inclusion}
        />)}
      </Group>
      {!!hidden.length && <div>
        <TextLink size="medium" onClick={openAllInclusionsModal}>
          +{hidden.length} more inclusions
        </TextLink>
      </div>}
    </HighlightedInclusionsContainer>
  </LabeledSheet>
}

export default connect<MappedStateProps, {}, {}, App.State>((state) => {
  return {
    isLuxPlusEnabled: getIsLuxPlusToursEnabled(state),
  }
})(TourV2HighlightedInclusions)
